/**
 * Push (Phase 3): listing → open item requests that should be notified.
 * Prefer strong/narrow; soft/exploratory/over_budget stay pull-list oriented.
 */
import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "@/lib/database.types";
import { normalizePreferenceAttrs } from "./preferenceAttrs";
import { isRequestEligibleForMatch } from "./matchEligibility";
import { scoreListingAgainstRequest } from "./matchScore";
import type {
  MatchBand,
  MatchListingInput,
  MatchRequestInput,
  MatchScoreBreakdown,
} from "./matchTypes";

type ServiceClient = SupabaseClient<Database>;

/** Cap fan-out per listing approve (protect approve path / channels). */
export const ITEM_REQUEST_MATCH_NOTIFY_LIMIT = 25;

/** Open requests scanned in the listing's category before scoring. */
const REQUEST_POOL_LIMIT = 200;

export type RequestMatchNotifyCandidate = {
  requestId: string;
  userId: string;
  requestTitle: string;
  request: MatchRequestInput;
  band: MatchBand;
  score: number;
  breakdown: MatchScoreBreakdown;
};

type RequestRow = Pick<
  Database["public"]["Tables"]["item_requests"]["Row"],
  | "id"
  | "user_id"
  | "title"
  | "category"
  | "subcategory"
  | "max_price"
  | "user_location"
  | "status"
  | "review_status"
  | "expires_at"
  | "preference_attrs"
>;

function rowToMatchRequest(row: RequestRow): MatchRequestInput | null {
  if (!row.id || !row.user_id || !row.category) return null;
  return {
    id: row.id,
    category: row.category,
    subcategory: row.subcategory,
    maxPrice: row.max_price,
    userLocation: row.user_location || "",
    status: row.status || "active",
    reviewStatus: row.review_status,
    expiresAt: row.expires_at,
    preferenceAttrs: normalizePreferenceAttrs(row.preference_attrs ?? {}),
  };
}

/**
 * Whether this scored band is eligible for push notify (U3 / Phase 3).
 * Strong only — soft/widen stay on pull list.
 */
export function isNotifyPushBand(band: MatchBand): boolean {
  return band === "strong";
}

/**
 * Given an approved listing, find open eligible requests to notify (strong first).
 * Excludes the seller's own requests.
 */
export async function findMatchingRequestsForListing(
  service: ServiceClient,
  listing: MatchListingInput & { sellerId?: string | null },
  options?: { limit?: number },
): Promise<RequestMatchNotifyCandidate[]> {
  const category = (listing.category || "").trim();
  if (!category || !listing.id) return [];

  const limit = Math.min(
    options?.limit ?? ITEM_REQUEST_MATCH_NOTIFY_LIMIT,
    ITEM_REQUEST_MATCH_NOTIFY_LIMIT,
  );

  const { data, error } = await service
    .from("item_requests")
    .select(
      "id,user_id,title,category,subcategory,max_price,user_location,status,review_status,expires_at,preference_attrs",
    )
    .eq("category", category)
    .eq("status", "active")
    .eq("review_status", "approved")
    .order("created_at", { ascending: false })
    .limit(REQUEST_POOL_LIMIT);

  if (error) {
    throw new Error(error.message);
  }

  const sellerId = listing.sellerId?.trim() || null;
  const candidates: RequestMatchNotifyCandidate[] = [];

  for (const row of data ?? []) {
    if (!row.user_id || (sellerId && row.user_id === sellerId)) continue;
    const request = rowToMatchRequest(row);
    if (!request || !isRequestEligibleForMatch(request)) continue;

    const scored = scoreListingAgainstRequest(request, listing);
    if (!scored || !isNotifyPushBand(scored.band)) continue;

    candidates.push({
      requestId: row.id,
      userId: row.user_id,
      requestTitle: (row.title || request.subcategory || "Your request").trim(),
      request,
      band: scored.band,
      score: scored.score,
      breakdown: scored.breakdown,
    });
  }

  candidates.sort((a, b) => b.score - a.score);
  return candidates.slice(0, limit);
}
